<template>
  <section class="section">
    <div class="content">
      <div class="appName">
        <div class="logo">
          <img src="/images/ABG logo.png" />
        </div>
        <h1 class="title">
          <span>{{ $t('login.appName[0]') }}</span>
          <span>{{ $t('login.appName[1]') }}</span>
        </h1>
      </div>

      <div class="loginForm">
        <h2 class="heading">{{ $t('login.heading') }}</h2>
        <form @submit.prevent="submit">
          <div class="inputWrapper">
            <div class="input">
              <input
                id="email"
                name="email"
                type="email"
                placeholder="{{ $t('login.inputEmail') }}"
                v-model="formFields.email"
                @input="() => input(formErrors.email, validateEmail)"
                @blur="validateEmail"
                :disabled="authenticationInProgress || authenticationSuccessful"
                :class="{ empty: formFields.email.length === 0, error: formErrors.email.length > 0 }"
              />
              <label for="email">
                {{ $t('login.inputEmail') }}
              </label>
            </div>
            <p class="errorMessage">
              {{ formErrors.email || '&nbsp;' }}
            </p>
          </div>
          <div class="inputWrapper">
            <div class="input">
              <input
                id="password"
                name="password"
                type="password"
                placeholder="{{ $t('login.inputPassword') }}"
                v-model="formFields.password"
                @input="() => input(formErrors.password, validatePassword)"
                @blur="validatePassword"
                :disabled="authenticationInProgress || authenticationSuccessful"
                :class="{ empty: formFields.password.length === 0, error: formErrors.password.length > 0 }"
              />
              <label for="password">
                {{ $t('login.inputPassword') }}
              </label>
            </div>
            <p class="errorMessage">
              {{ formErrors.password || '&nbsp;' }}
            </p>
          </div>
          <button
            type="submit"
            :disabled="authenticationInProgress || authenticationSuccessful"
            class="button"
          >
            {{ $t('login.buttonSubmit') }}
          </button>
          <div
            class="processingOverlay"
            v-if="authenticationInProgress"
          >
            <font-awesome-icon icon="rotate"/>
          </div>
        </form>
        <div
          class="successOverlay"
          v-if="authenticationSuccessful"
        >
          <div class="redirectLoadingSpinner"/>
          <p>{{ $t('login.successMessage') }}</p>
          <a
            href="/"
            @click="redirectToHome"
          >
            {{ $t('login.linkHomepage') }}
            <font-awesome-icon icon="arrow-right"/>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  .section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
  }

  .content {
    background-color: var(--secondary-background-color);
    box-shadow: 6px 6px 22px -15px rgba(130, 130, 130, 1);
    border-radius: 5px;
  }

  .appName {
    display: flex;
    flex-wrap: wrap;
    padding: 20px;
    border-bottom: 1px solid #e3e3e3;
  }

  .logo > img {
    width: 100px;
  }

  .title {
    max-width: 300px;
    margin: auto;
    font-size: 2.5rem;
    font-weight: 800;
    color: var(--tertiary-color);
  }

  .title > span {
    display: block;
  }
  
  .loginForm {
    position: relative;
  }

  .heading {
    margin: 0;
    padding: 20px;
    font-size: 1.4rem;
    font-weight: 500;
  }

  .loginForm > form {
    position: relative;
    display: flex;
    width: 300px;
    max-width: 300px;
    flex-direction: column;
    padding: 20px;
  }

  .processingOverlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 6rem;
    color: var(--primary-color);
    background-color: rgba(255, 255, 255, 0.9);
  }

  .processingOverlay > svg {
    animation-name: spinner;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }

  @keyframes spinner {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .successOverlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--secondary-background-color);
  }

  .redirectLoadingSpinner {
    width: 30px;
    height: 30px;
    border: 5px solid var(--primary-color);
    border-radius: 100%;
    clip-path: polygon(50% 50%, 50% -30%, 50% -30%, 50% -30%, 50% -30%, 50% -30%, 50% -30%, 50% -30%, 50% -30%, 50% -30%);
    animation: drawCircle;
    animation-duration: 3s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  @keyframes drawCircle {
    0% { clip-path: polygon(50% 50%, 50% -20%, 50% -20%, 50% -20%, 50% -20%, 50% -20%, 50% -20%, 50% -20%, 50% -20%, 50% -20%); }
    12.5% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%); }
    25% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 120% 50%, 120% 50%, 120% 50%, 120% 50%, 120% 50%, 120% 50%); }
    37.5% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%); }
    50% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 50% 120%, 50% 120%, 50% 120%, 50% 120%, 50% 120%); }
    62.5% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 50% 120%, 0% 100%, 0% 100%, 0% 100%, 0% 100%); }
    75% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 50% 120%, 0% 100%, -20% 50%, -20% 50%, -20% 50%); }
    87.5% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 50% 120%, 0% 100%, -20% 50%, 0% 0%, 0% 0%); }
    100% { clip-path: polygon(50% 50%, 50% -20%, 100% 0%, 120% 50%, 100% 100%, 50% 120%, 0% 100%, -20% 50%, 0% 0%, 50% -20%); }
  }

  .successOverlay > p {
    margin: 20px 0;
    padding: 0;
    font-size: 1.6rem;
    font-weight: 400;
  }

  .successOverlay > a {
    font-size: 1.6rem;
    color: var(--primary-color);
    text-decoration: none;
    transition-property: color;
    transition-duration: 0.2s;
    z-index: 1;
  }

  .successOverlay > a > svg {
    position: relative;
    left: 0;
    color: var(--primary-color);
    transition-property: left, color;
    transition-duration: 0.2s;
  }

  .successOverlay > a:hover {
    color: var(--secondary-color);
  }

  .successOverlay > a:hover > svg {
    left: 5px;
    color: var(--secondary-color);
  }

  .inputWrapper {
    position: relative;
    margin: 10px 0 20px 0;
  }

  .input > input {
    height: 5rem;
    width: 100%;
    box-sizing: border-box;
    margin: 10px 0 5px 0;
    padding: 10px;
    font-family: inherit;
    font-size: 1.4rem;
    color: inherit;
    background-color: var(--secondary-background-color);
    border: none;
    border-bottom: 2px solid var(--primary-color);
    border-radius: 3px;
    box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.1);
    outline: none;
    transition-property: border-color box-shadow;
    transition-duration: 0.3s;
  }

  .input > input:focus {
    border-color: var(--secondary-color);
    box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.1), inset 0 0 2px var(--secondary-color);
  }

  .input > input::placeholder {
    color: transparent;
  }

  .input > label {
    position: absolute;
    margin: 0;
    top: 0;
    left: 0;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 0;
    pointer-events: none;
    transition-property: top, left;
    transition-duration: 0.3s;
  }

  .input > input.empty + label {
    top: 3.5rem;
    left: 2rem;
  }

  .input > input:focus + label,
  .input > input:-internal-autofill-selected + label {
    top: 0;
    left: 0;
  }

  .input > input.error {
    border-color: var(--error-color);
  }

  .errorMessage {
    height: 10px;
    margin: 0;
    padding: 0;
    font-size: 1.2rem;
    line-height: 1;
    color: var(--error-color);
  }

  .button {
    display: block;
    width: 100%;
    height: 50px;
    margin-top: 20px;
    padding: 10px 20px;
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--primary-text-color-white);
    background-color: var(--primary-color);
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .button:hover,
  .button:focus,
  .button:active {
    background-color: var(--secondary-color);
  }
</style>

<script>
  export default {
    data() {
      return {
        formFields: {
          email: '',
          password: ''
        },
        formErrors: {
          email: '',
          password: ''
        },
        authenticationInProgress: false,
        authenticationSuccessful: false,
        redirectTimeout: null
      }
    },
    methods: {
      input(error, validator) {
        if (error.length > 0) {
          validator();
        }
      },
      validateEmail() {
        const email = this.formFields.email;
        let error = '';
        
        if (email.length === 0) {
          error = this.$t('login.errorNoEmail');
        } else if (!EmailValidator.validate(email)) {
          error = this.$t('login.errorInvalidEmail');
        }

        this.formErrors.email = error;
      },
      validatePassword() {
        const password = this.formFields.password;
        let error = '';

        if (password.length === 0) {
          error = this.$t('login.errorNoPassword');
        } else if (password.length < 2) {
          error = this.$t('login.errorTooShortPassword');
        }

        this.formErrors.password = error;
      },
      redirectToHome(event) {
        event.preventDefault();
        clearTimeout(this.redirectTimeout);
        window.location.href = '/';
      },
      submit: async function() {
        this.validateEmail();
        this.validatePassword();

        if (this.formErrors.email.length > 0 || this.formErrors.password.length > 0) {
          return;
        }

        this.authenticationInProgress = true;

        const response = await fetch('/api/user/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.formFields)
        });

        const responseJson = await response.json();

        switch (response.status) {
          case 200:
            this.authenticationSuccessful = true;
            this.redirectTimeout = setTimeout(() => {
              window.location.href = '/';
            }, 3000);
            break;
          case 422:
            this.formErrors.email = responseJson?.email?.join(' ') ?? '';
            this.formErrors.password = responseJson?.password?.join(' ') ?? '';
            break;
          case 401:
            this.formErrors.email = this.formErrors.password = this.$t('login.errorInvalidCredentials');
            break;
          default:
            this.formErrors.email = this.formErrors.password = this.$t('login.errorServerGeneric');
        }

        this.authenticationInProgress = false;
      }
    }
  }
</script>
